import { useState } from "react";

import { formatNumber } from "@buildpy/core/lib/format";
import { draftDays, DraftDaysInputError } from "@buildpy/core/modules/process-standard/calc";
import { crewLabel, soloLabel } from "@buildpy/core/modules/process-standard/draft";
import type { ProcessStandardArticle, ProcessStandardItem } from "@buildpy/core/modules/process-standard/types";
import { BUILTIN_PHASES } from "@buildpy/core/modules/template/phases";
import { Button } from "@buildpy/ui/button";
import SectionCard from "@buildpy/ui/section-card";

import ArticleBlock from "./article-block";
import ProposalDialog from "./proposal-dialog";

// 계산 예의 수량 — 화면에서 판 수치를 감 잡는 기준 면적.
export const EXAMPLE_M2 = 30;

export interface StandardGroup {
  phaseCode: string;
  phaseName: string;
  items: ProcessStandardItem[];
}

// 공정 순서(내장 공정 sort_order)대로 묶고 그 안은 항목 sort_order — 모르는 공정 코드는 뒤에 코드 이름 그대로.
export const groupByPhase = (items: ProcessStandardItem[]): StandardGroup[] => {
  const order = new Map(BUILTIN_PHASES.map((phase) => [phase.code as string, phase]));
  const groups = new Map<string, ProcessStandardItem[]>();
  for (const item of [...items].sort((a, b) => a.sort_order - b.sort_order)) groups.set(item.phase_code, [...(groups.get(item.phase_code) ?? []), item]);
  return [...groups.entries()]
    .map(([phaseCode, list]) => ({ phaseCode, phaseName: order.get(phaseCode)?.name ?? phaseCode, items: list }))
    .sort((a, b) => (order.get(a.phaseCode)?.sort_order ?? Infinity) - (order.get(b.phaseCode)?.sort_order ?? Infinity));
};

// 「30m² → N일(표준 인원) · 기능공 1명이면 M일」 — 계수 1 · 판 수치가 계산을 못 하면 null.
export const exampleText = (item: ProcessStandardItem): string | null => {
  try {
    const days = draftDays({ quantityM2: EXAMPLE_M2, crew: item.crew_size, outputPerPersonDay: item.output_per_person_day, factor: 1 });
    return `${EXAMPLE_M2}m² → ${formatNumber(days.crewDays)}일(${crewLabel(item.crew_size, item.laborer_size)}) · ${soloLabel(item.laborer_size)} ${formatNumber(days.soloDays)}일`;
  } catch (error) {
    if (error instanceof DraftDaysInputError) return null;
    throw error;
  }
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-y-0.5">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm font-medium tabular-nums">{value}</dd>
  </div>
);

const ItemCard = ({ item, article, versionId }: { item: ProcessStandardItem; article: ProcessStandardArticle | undefined; versionId: string }) => {
  const example = exampleText(item);
  const [proposing, setProposing] = useState(false);
  return (
    <SectionCard
      actions={
        <Button onClick={() => setProposing(true)} size="sm" variant="outline">
          숫자 제안
        </Button>
      }
      as="article"
      description={item.code}
      title={item.name}>
      <dl className="grid grid-cols-3 gap-3">
        <Stat label="표준 인원" value={crewLabel(item.crew_size, item.laborer_size)} />
        <Stat label="1인 1일 작업량" value={`${formatNumber(item.output_per_person_day)}m²`} />
        <Stat label="양생" value={`${formatNumber(item.curing_days)}일`} />
      </dl>
      {example && <p className="mt-3 text-sm tabular-nums">계산 예 · {example}</p>}
      <p className="mt-3 break-words text-xs text-muted-foreground">출처 · {item.source}</p>
      {article && <ArticleBlock key={`${article.id}:${article.updated_at}`} article={article} />}
      <ProposalDialog item={item} onOpenChange={setProposing} open={proposing} versionId={versionId} />
    </SectionCard>
  );
};

interface Props {
  versionId: string;
  items: ProcessStandardItem[];
  articles: ProcessStandardArticle[];
}

// 공정별 묶음 — 글은 항목 code 와 같은 code 로 잇는다(시드 규칙).
const StandardsList = ({ versionId, items, articles }: Props) => {
  const byCode = new Map(articles.map((article) => [article.code, article]));
  return (
    <div className="flex flex-col gap-y-6">
      {groupByPhase(items).map((group) => (
        <section key={group.phaseCode} aria-label={group.phaseName} className="flex flex-col gap-y-3">
          <h2 className="text-base font-semibold">
            {group.phaseName} <span className="text-sm font-normal text-muted-foreground">{group.items.length}개</span>
          </h2>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {group.items.map((item) => (
              <ItemCard key={item.id} article={byCode.get(item.code)} item={item} versionId={versionId} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default StandardsList;
